<template>
  <div>
    <!-- Linha 1: Estatísticas Afiliados -->
    <h5 class="mb-2">Estatísticas Afiliados</h5>
    <b-row class="mb-3">
      <b-col md="3" sm="6" v-for="(stat, index) in estatisticasAfiliado" :key="'stat-afil-'+index">
        <b-card class="text-center shadow-sm border-0 h-80">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div :class="`rounded-circle p-2 bg-light-${stat.color}`">
                <feather-icon :icon="stat.icon" :class="`text-${stat.color}`" size="20"/>
              </div>
            </div>
            <div>
              <p class="card-text small text-muted mb-0">{{ stat.title }}</p>
              <h5 class="font-weight-bolder mb-0">{{ stat.value }}</h5>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Linha 2: Gráfico Comissões e Progresso -->
    <b-row class="mb-3">
      <!-- Card Comissões Mensais - Largura completa -->
      <b-col cols="12">
        <b-card no-body class="shadow-sm border-0 mb-3">
          <b-card-header class="d-flex justify-content-between align-items-center pb-2">
            <b-card-title>Comissões Mensais {{ new Date().getFullYear() }}</b-card-title>
            <a href="#" class="chart-link small">Este Ano</a>
          </b-card-header>
          <b-card-body>
            <line-chart :chart-data="chartDataComissoes" :options="chartOptionsComissoes" :height="200" />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Linha 4: Estatísticas de Desempenho e Central de Ajuda -->
    <b-row class="mb-3">
      <!-- Card Estatísticas de Desempenho -->
      <b-col lg="6" md="12">
        <b-card no-body class="shadow-sm border-0 h-100 performance-card">
          <b-card-header class="bg-gradient-primary">
            <b-card-title class="text-white mb-0 d-flex align-items-center">
              <div class="performance-icon-wrapper mr-2">
                <feather-icon icon="TrendingUpIcon" size="20" class="text-white"/>
              </div>
              <div>
                <span class="performance-title">Estatísticas de Desempenho</span>
                <small class="d-block text-white-50 mt-1">Resumo das suas métricas</small>
              </div>
            </b-card-title>
          </b-card-header>
          <b-card-body class="p-0">
            <div v-for="(stat, index) in estatisticasDesempenho" :key="'perf-'+index" class="performance-item d-flex align-items-center p-3 border-bottom position-relative">
              <!-- Indicador lateral colorido -->
              <div :class="`performance-indicator bg-${stat.color}`"></div>
              
              <!-- Ícone -->
              <div class="performance-icon-container mr-3">
                <div :class="`performance-icon bg-light-${stat.color}`">
                  <feather-icon :icon="stat.icon" :class="`text-${stat.color}`" size="18"/>
                </div>
              </div>
              
              <!-- Conteúdo -->
              <div class="flex-grow-1">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="performance-label mb-1">{{ stat.label }}</h6>
                    <small class="text-muted performance-description">
                      {{ getPerformanceDescription(stat.label) }}
                    </small>
                  </div>
                  <div class="text-right">
                    <span class="performance-value font-weight-bold">{{ stat.value }}</span>
                    <div v-if="stat.trend" class="performance-trend">
                      <feather-icon 
                        :icon="stat.trend > 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'" 
                        :class="stat.trend > 0 ? 'text-success' : 'text-danger'" 
                        size="12"
                        class="mr-1"
                      />
                      <small :class="stat.trend > 0 ? 'text-success' : 'text-danger'">
                        {{ Math.abs(stat.trend) }}%
                      </small>
                    </div>
                  </div>
                </div>
                
                <!-- Barra de progresso opcional -->
                <div v-if="stat.progress" class="performance-progress mt-2">
                  <div class="progress" style="height: 4px;">
                    <div 
                      class="progress-bar" 
                      :class="`bg-${stat.color}`"
                      :style="`width: ${stat.progress}%`"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Card Central de Ajuda -->
      <b-col lg="6" md="12">
        <b-card no-body class="shadow-sm border-0 h-100 performance-card">
          <b-card-header class="bg-gradient-info">
            <b-card-title class="text-white mb-0 d-flex align-items-center">
              <div class="help-icon-wrapper mr-2">
                <feather-icon icon="HelpCircleIcon" size="20" class="text-white"/>
              </div>
              <div>
                <span class="help-title">Central de Ajuda</span>
                <small class="d-block text-white-50 mt-1">Suporte rápido e eficiente</small>
              </div>
            </b-card-title>
          </b-card-header>
          <b-card-body class="p-0">
            <div class="help-items-container-full">
              <div 
                v-for="(item, index) in centralAjuda" 
                :key="'ajuda-'+index"
                class="performance-item d-flex align-items-center p-3 border-bottom position-relative cursor-pointer"
                @click="$router.push({ name: item.route })"
              >
                <!-- Indicador lateral colorido -->
                <div :class="`performance-indicator bg-${item.color}`"></div>
                
                <!-- Container principal -->
                <div class="d-flex align-items-start">
                  <!-- Ícone melhorado -->
                  <div class="performance-icon-container mr-3">
                    <div :class="`performance-icon bg-light-${item.color}`">
                      <feather-icon :icon="item.icon" :class="`text-${item.color}`" size="18"/>
                    </div>
                  </div>
                  
                  <!-- Conteúdo -->
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="help-content">
                        <h6 :class="`performance-label text-${item.color} font-weight-bold mb-1`">
                          {{ item.titulo }}
                        </h6>
                        <p class="performance-description small text-muted mb-2">
                          {{ item.descricao }}
                        </p>
                        <div class="help-item-meta">
                          <small class="text-muted">
                            <feather-icon icon="ClockIcon" size="12" class="mr-1"/>
                            Disponível 24/7
                          </small>
                        </div>
                      </div>
                      
                      <!-- Seta de navegação -->
                      <div class="help-arrow">
                        <feather-icon icon="ChevronRightIcon" :class="`text-${item.color}`" size="16"/>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Status indicator -->
                <div class="help-status-dot"></div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BButton,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { Line } from 'vue-chartjs'
import useDashboardAfiliado from '@/composables/useDashboardAfiliado'
import { currency } from '@core/utils/filter'
import Vue from 'vue'
import Clipboard from 'v-clipboard'

Vue.use(Clipboard)

// Componente de gráfico de linha personalizado
const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    if (this.chartData && this.chartData.datasets) {
      this.renderChart(this.chartData, this.options)
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data.chart) {
          this.$data.chart.destroy()
        }
        if (newData && newData.datasets) {
          this.renderChart(newData, this.options)
        }
      },
    },
    options: {
      deep: true,
      handler(newOptions) {
        if (this.$data.chart) {
          this.$data.chart.destroy()
          this.renderChart(this.chartData, newOptions)
        }
      },
    },
  },
}

export default {
  components:
   {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BButton,
    FeatherIcon,
    LineChart,
  },
  filters: {
    currency,
  },
  setup() {
    const {
      // Estados
      isLoading,
      
      // Dados computados
      estatisticasAfiliado,
      minhasAfiliacao,
      ultimasVendas,
      proximoPagamentoReal,
      estatisticasDesempenho,
      
      // Dados dos gráficos
      chartDataComissoes,
      chartDataCliques,
      chartOptionsComissoes,
      chartOptionsCliques,
      
      // Dados estáticos
      progresso,
      proximoPagamento,
      produtosDivulgar,
      centralAjuda,
      linksAtivos,
    } = useDashboardAfiliado()

    // Métodos para lidar com clipboard
    const onCopySuccess = () => {
      // Aqui você pode adicionar uma notificação de sucesso se necessário
      console.log('Link copiado com sucesso!')
    }

    const onCopyError = () => {
      // Aqui você pode adicionar uma notificação de erro se necessário
      console.log('Erro ao copiar link')
    }

    // Método para obter descrição das estatísticas
    const getPerformanceDescription = label => {
      const descriptions = {
        'Cliques no Link': 'Número total de cliques nos seus links',
        Conversões: 'Vendas geradas através dos seus links',
        'Taxa de Conversão': 'Percentual de vendas que foram pagas',
        'Comissão Total': 'Total acumulado em comissões',
        'Links Ativos': 'Quantidade de links ativos no momento',
        'Vendas Este Mês': 'Vendas realizadas no mês atual',
      }
      return descriptions[label] || 'Métrica de desempenho'
    }

    return {
      // Estados
      isLoading,
      
      // Dados computados
      estatisticasAfiliado,
      minhasAfiliacao,
      ultimasVendas,
      proximoPagamentoReal,
      estatisticasDesempenho,
      
      // Dados dos gráficos
      chartDataComissoes,
      chartDataCliques,
      chartOptionsComissoes,
      chartOptionsCliques,
      
      // Dados estáticos
      progresso,
      proximoPagamento,
      produtosDivulgar,
      centralAjuda,
      linksAtivos,

      // Métodos
      onCopySuccess,
      onCopyError,
      getPerformanceDescription,
    }
  },
}
</script>

<style scoped>
.chart-link {
  color: #6e6b7b;
  text-decoration: none;
}
.chart-link:hover {
  color: #5e5873;
}
.card-text {
  line-height: 1.2;
}
.h-100 {
  height: 100% !important;
}
.mb-3 {
  margin-bottom: 1rem !important;
}
.pb-0 {
  padding-bottom: 0 !important;
}
/* Ajustes para o alerta */
.alert.alert-light-primary {
  background-color: rgba(115, 103, 240, 0.12);
  border-color: rgba(115, 103, 240, 0.2);
  color: #7367f0;
}
.alert-heading {
  color: inherit;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.help-item {
  transition: all 0.3s ease-in-out;
  border: 1px solid transparent;
}
.help-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: rgba(115, 103, 240, 0.2);
}

.afiliacao-item {
  transition: background-color 0.2s ease-in-out;
}
.afiliacao-item:hover {
  background-color: rgba(115, 103, 240, 0.05);
  border-radius: 8px;
}

.btn-copy-link {
  transition: all 0.2s ease-in-out;
}
.btn-copy-link:hover {
  transform: scale(1.05);
}

.cursor-pointer {
  cursor: pointer;
}

/* Estilos para o card de performance melhorado */
.performance-card {
  overflow: hidden;
  transition: all 0.3s ease;
}

.performance-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%);
  border: none;
}

.performance-icon-wrapper {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.performance-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.performance-item {
  transition: all 0.2s ease;
  position: relative;
}

.performance-item:hover {
  background-color: rgba(115, 103, 240, 0.02);
  transform: translateX(2px);
}

.performance-item:last-child {
  border-bottom: none !important;
}

.performance-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.performance-item:hover .performance-indicator {
  opacity: 1;
}

.performance-icon-container {
  position: relative;
}

.performance-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.performance-icon::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transform: rotate(45deg);
  transition: all 0.6s ease;
  opacity: 0;
}

.performance-item:hover .performance-icon::before {
  opacity: 1;
  animation: shimmer 1.5s ease-in-out;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

.performance-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0;
}

.performance-description {
  font-size: 0.8rem;
  line-height: 1.2;
}

.performance-trend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2px;
}

.performance-progress {
  margin-top: 8px;
}

.performance-progress .progress {
  background-color: #f3f2f7;
  border-radius: 10px;
  overflow: hidden;
}

.performance-progress .progress-bar {
  border-radius: 10px;
  background: linear-gradient(90deg, rgba(115, 103, 240, 0.6), rgba(115, 103, 240, 1));
  transition: width 0.8s ease;
}

.performance-footer {
  border-top: 1px solid #ebe9f1;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

/* Cores específicas para diferentes métricas */
.bg-light-primary { background: linear-gradient(135deg, rgba(115, 103, 240, 0.1), rgba(115, 103, 240, 0.05)); }
.bg-light-success { background: linear-gradient(135deg, rgba(40, 199, 111, 0.1), rgba(40, 199, 111, 0.05)); }
.bg-light-info { background: linear-gradient(135deg, rgba(0, 207, 232, 0.1), rgba(0, 207, 232, 0.05)); }
.bg-light-warning { background: linear-gradient(135deg, rgba(255, 159, 67, 0.1), rgba(255, 159, 67, 0.05)); }
.bg-light-danger { background: linear-gradient(135deg, rgba(234, 84, 85, 0.1), rgba(234, 84, 85, 0.05)); }

.afiliacao-item {
  transition: background-color 0.2s ease-in-out;
  min-height: 60px;
}
.afiliacao-item:hover {
  background-color: rgba(115, 103, 240, 0.05);
}

.product-avatar {
  transition: all 0.2s ease-in-out;
}
.afiliacao-item:hover .product-avatar {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.3);
}

.btn-copy-compact {
  padding: 0.25rem 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-copy-compact:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.min-width-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.font-weight-semibold {
  font-weight: 600;
}

/* Compactação do header */
.bg-light {
  background-color: #f8f9fa !important;
}

/* Ajustes para mobile */
@media (max-width: 768px) {
  .afiliacao-item {
    padding: 1rem !important;
    min-height: auto;
  }
  
  .product-avatar {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
  }
  
  .btn-copy-compact {
    width: 28px;
    height: 28px;
  }
}

/* Responsividade para cards */
@media (max-width: 768px) {
  .help-item {
    margin-bottom: 1rem !important;
  }
}

/* Botão Ver Todas alinhado à direita */
.btn-ver-todas {
  white-space: nowrap;
  flex-shrink: 0;
}

/* Garantir que o header do card distribua corretamente o espaço */
.card-header .w-100 {
  width: 100% !important;
}

.card-header .flex-grow-1 {
  flex-grow: 1 !important;
}

.card-header .ml-auto {
  margin-left: auto !important;
}

/* Estilos para os novos elementos de status */
.status-container {
  gap: 0.5rem;
}

.status-badge-wrapper {
  display: flex;
  justify-content: center;
}

.status-badge-primary {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.activity-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s infinite;
}

.status-dot-active {
  background-color: #28c76f;
  box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.7);
}

.status-dot-inactive {
  background-color: #ea5455;
  box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.7);
  animation: none;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(40, 199, 111, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0);
  }
}

.status-text {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Estilos para o card de Central de Ajuda melhorado */
.help-center-card {
  overflow: hidden;
  transition: all 0.3s ease;
}

.help-center-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #00cfe8 0%, #2dd4ed 100%);
  border: none;
}

.help-icon-wrapper {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.help-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.help-item-enhanced {
  transition: all 0.2s ease;
  position: relative;
  background: #ffffff;
}

.help-item-enhanced:hover {
  background-color: rgba(0, 207, 232, 0.02);
  transform: translateX(3px);
}

.help-item-enhanced:last-child {
  border-bottom: none !important;
}

.help-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.help-item-enhanced:hover .help-indicator {
  opacity: 1;
}

.help-icon-container {
  position: relative;
}

.help-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
}

.help-item-enhanced:hover .help-icon {
  transform: scale(1.05);
  border-color: rgba(0, 207, 232, 0.2);
  box-shadow: 0 4px 12px rgba(0, 207, 232, 0.15);
}

.help-content {
  flex-grow: 1;
}

.help-item-title {
  font-size: 0.95rem;
  transition: color 0.2s ease;
}

.help-item-description {
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.help-item-meta {
  opacity: 0;
  transform: translateY(5px);
  transition: all 0.2s ease;
}

.help-item-enhanced:hover .help-item-meta {
  opacity: 1;
  transform: translateY(0);
}

.help-arrow {
  transition: all 0.2s ease;
  opacity: 0.5;
}

.help-item-enhanced:hover .help-arrow {
  opacity: 1;
  transform: translateX(3px);
}

.help-status-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: #28c76f;
  border-radius: 50%;
  animation: pulse-help 2s infinite;
}

@keyframes pulse-help {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(40, 199, 111, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0);
  }
}

.help-quick-contact {
  border-top: 1px solid #ebe9f1;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.help-status-badge .badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #28c76f, #48da89);
  animation: glow-success 2s ease-in-out infinite alternate;
}

@keyframes glow-success {
  from {
    box-shadow: 0 0 5px rgba(40, 199, 111, 0.5);
  }
  to {
    box-shadow: 0 0 10px rgba(40, 199, 111, 0.8);
  }
}

.help-action-btn {
  transition: all 0.2s ease;
  border-radius: 8px;
  font-weight: 500;
}

.help-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Cores específicas para diferentes tipos de ajuda */
.bg-primary { background-color: #7367f0 !important; }
.bg-success { background-color: #28c76f !important; }
.bg-info { background-color: #00cfe8 !important; }
.bg-warning { background-color: #ff9f43 !important; }
.bg-danger { background-color: #ea5455 !important; }

/* Modo escuro - Central de Ajuda */
::v-deep(.dark-layout) .help-center-card,
::v-deep(.dark-layout) .help-item-enhanced {
  background-color: #283046 !important;
  border-color: #3b4253 !important;
}

::v-deep(.dark-layout) .help-center-card.card,
::v-deep(.dark-layout) .help-center-card .card-body,
::v-deep(.dark-layout) .help-center-card .help-items-container-full {
  background-color: #283046 !important;
}

::v-deep(.dark-layout) .help-item-enhanced:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

::v-deep(.dark-layout) .help-item-title {
  color: #d0d2d6;
}

::v-deep(.dark-layout) .help-item-description,
::v-deep(.dark-layout) .help-item-meta small {
  color: #a6a9b4 !important;
}

::v-deep(.dark-layout) .help-item-enhanced .text-muted {
  color: #a6a9b4 !important;
}

::v-deep(.dark-layout) .help-icon {
  background-color: #1e2433;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

::v-deep(.dark-layout) .help-arrow svg {
  opacity: 0.8;
}

::v-deep(.dark-layout) .help-status-dot {
  background: #28c76f;
  box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.5);
}
</style>